/**
 * Putt-to-TV latency, measured per putt (2026-10-16: Cole sees 1-2 s between
 * a putt and the Apple TV drawing it; every earlier number was an estimate).
 *
 * The chain and who stamps what:
 *
 *   gate rear beam -> (gate hold, firmware, NOT measured here)
 *   phone BLE receipt  bleRxMs        phone clock   (app/LagKing/src/tv/puttLatency.ts)
 *   publish enqueued   enqueuedMs     phone clock   (tvDisplayStore.publish)
 *   callable started   callStartMs    phone clock   (the attempt that landed)
 *   server handler     serverMs       server clock  (publishTvState `now`, doc.updatedAtMs)
 *   server commit      commitMs       server clock  (doc.updatedAt, the serverTimestamp)
 *   TV snapshot        arrivalMs      TV clock      (tvSessionClient onSnapshot)
 *   TV first frame     firstFrameMs   TV clock      (SceneContent's first replay frame)
 *   callable resolved  callEndMs      phone clock   (phone only; not on the event)
 *
 * Clock skew: both devices are NTP-synced, so the raw cross-device numbers
 * are usually within tens of ms, but never assume it. Each side estimates its
 * offset to the server clock from a callable round trip (server `now` between
 * the send and the reply): offset = server - midpoint, error = +/- half the
 * round trip. The minimum-round-trip sample of the last 10 minutes wins.
 * Corrected stages put every stamp on the server clock; the error bound says
 * how far a corrected cross-device stage can be off. Same-device stages
 * (queue wait, callable round trip, arrival -> first frame) are exact.
 *
 * Pure: the phone, the page, the Apple TV app and node tests share it. No ids
 * beyond the putt event id, no names, no secrets.
 */
export const TV_LATENCY_VERSION = 1;

/** Phone stamps that ride on the putt event as `event.lat` (phone clock, ms). */
export interface TvPuttLatencyStamp {
  v: number;
  /** BLE notification receipt; null for a putt the gate did not send (CPU turn). */
  bleRxMs: number | null;
  /** True when the receipt was the phone's last BLE putt, not this notification's own. */
  bleRxApprox?: boolean;
  builtMs: number;
  enqueuedMs?: number;
  /** The publish sequence carrying the event. */
  seq?: number;
  callStartMs?: number;
  /** The phone's best server-clock offset at send time (server - phone). */
  offMs?: number | null;
  offErrMs?: number | null;
}

/** What the TV measured for one putt event (TV clock unless noted). */
export interface TvReceiverLatencyRecord {
  id: string;
  /** "appletv" or "web". */
  rx: string;
  stamp: TvPuttLatencyStamp | null;
  /** Sequence of the session document that first carried the event. */
  docSeq: number | null;
  /** Server clock: publishTvState's handler time and the commit time. */
  serverMs: number | null;
  commitMs: number | null;
  arrivalMs: number;
  firstFrameMs: number | null;
  fromCache: boolean;
  /** TV offset to the server clock (server - TV) when the event arrived. */
  offMs: number | null;
  offErrMs: number | null;
  reported: boolean;
}

/** The compact form the TV sends back in its heartbeat (functions sanitizes the same keys). */
export interface TvLatencyReport {
  id: string;
  rx: string;
  /** Phone stamps echoed back so the report stands alone. */
  ble: number | null;
  call: number | null;
  poff: number | null;
  poffErr: number | null;
  seq: number | null;
  docSeq: number | null;
  srv: number | null;
  cmt: number | null;
  arr: number;
  ff: number | null;
  off: number | null;
  offErr: number | null;
  cache: boolean;
}

export const TV_LATENCY_REPORT_KEYS = [
  'id', 'rx', 'ble', 'call', 'poff', 'poffErr', 'seq', 'docSeq', 'srv', 'cmt', 'arr', 'ff', 'off', 'offErr', 'cache',
] as const;

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function round(value: number | null): number | null {
  return value == null ? null : Math.round(value);
}

/** Read `event.lat` defensively: an older phone sends none, a newer one more. */
export function readTvPuttLatencyStamp(value: unknown): TvPuttLatencyStamp | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const builtMs = finite(raw.builtMs);
  if (builtMs == null) return null;
  return {
    v: finite(raw.v) ?? 0,
    bleRxMs: finite(raw.bleRxMs),
    ...(raw.bleRxApprox === true ? { bleRxApprox: true } : {}),
    builtMs,
    ...(finite(raw.enqueuedMs) != null ? { enqueuedMs: finite(raw.enqueuedMs)! } : {}),
    ...(finite(raw.seq) != null ? { seq: finite(raw.seq)! } : {}),
    ...(finite(raw.callStartMs) != null ? { callStartMs: finite(raw.callStartMs)! } : {}),
    offMs: finite(raw.offMs),
    offErrMs: finite(raw.offErrMs),
  };
}

// ---------------------------------------------------------------------------
// Clock offset to the server

export interface ClockOffset {
  /** server clock - local clock, ms. */
  offsetMs: number;
  /** +/- bound: half the round trip of the sample. */
  errorMs: number;
}

export interface ClockOffsetEstimator {
  /** One callable round trip: local send, server time inside, local reply. */
  sample: (sentMs: number, serverMs: unknown, receivedMs: number) => void;
  current: (nowMs?: number) => ClockOffset | null;
}

export function createClockOffsetEstimator(options: { windowMs?: number; capacity?: number; now?: () => number } = {}): ClockOffsetEstimator {
  const windowMs = options.windowMs ?? 10 * 60_000;
  const capacity = options.capacity ?? 12;
  const now = options.now ?? Date.now;
  const samples: Array<ClockOffset & { atMs: number }> = [];
  return {
    sample(sentMs, serverMs, receivedMs) {
      const server = finite(serverMs);
      if (server == null || !Number.isFinite(sentMs) || !Number.isFinite(receivedMs) || receivedMs < sentMs) return;
      const half = (receivedMs - sentMs) / 2;
      samples.push({ offsetMs: server - (sentMs + half), errorMs: half, atMs: receivedMs });
      while (samples.length > capacity) samples.shift();
    },
    current(nowMs = now()) {
      let best: ClockOffset | null = null;
      for (const sample of samples) {
        if (nowMs - sample.atMs > windowMs) continue;
        if (!best || sample.errorMs < best.errorMs) best = { offsetMs: sample.offsetMs, errorMs: sample.errorMs };
      }
      return best ? { offsetMs: Math.round(best.offsetMs), errorMs: Math.round(best.errorMs) } : null;
    },
  };
}

// ---------------------------------------------------------------------------
// The stages

export interface TvLatencyInputs {
  bleRxMs: number | null;
  enqueuedMs?: number | null;
  callStartMs?: number | null;
  callEndMs?: number | null;
  phoneOffMs?: number | null;
  phoneOffErrMs?: number | null;
  serverMs?: number | null;
  commitMs?: number | null;
  arrivalMs?: number | null;
  firstFrameMs?: number | null;
  tvOffMs?: number | null;
  tvOffErrMs?: number | null;
}

/** Every stage in ms; null where a stamp is missing. "Raw" cross-device
 *  numbers assume the clocks agree; the others are on the server clock. */
export interface TvLatencyStages {
  /** BLE receipt -> publish enqueued (app handling). Phone clock, exact. */
  bleToEnqueueMs: number | null;
  /** Enqueued -> the callable attempt that landed (queue wait, retries). Exact. */
  queueWaitMs: number | null;
  /** Callable start -> resolved, phone clock, exact. */
  callRttMs: number | null;
  /** Callable start -> server handler (network + cold start). Corrected. */
  phoneToServerMs: number | null;
  /** Handler -> commit (the transaction). Server clock, exact. */
  serverCommitMs: number | null;
  /** Commit -> TV snapshot (Firestore listen delivery). Corrected. */
  commitToTvMs: number | null;
  /** TV snapshot -> first replay frame. TV clock, exact. */
  tvToFrameMs: number | null;
  /** BLE receipt -> TV snapshot. */
  bleToTvRawMs: number | null;
  bleToTvMs: number | null;
  /** BLE receipt -> TV first frame: the number Cole feels (plus the gate hold). */
  totalRawMs: number | null;
  totalMs: number | null;
  /** +/- bound on a corrected cross-device stage (phone error + TV error). */
  skewErrMs: number | null;
}

function diff(later: number | null | undefined, earlier: number | null | undefined): number | null {
  return later == null || earlier == null ? null : Math.round(later - earlier);
}

export function tvLatencyStages(input: TvLatencyInputs): TvLatencyStages {
  const phoneOff = finite(input.phoneOffMs);
  const tvOff = finite(input.tvOffMs);
  const onServer = (local: number | null | undefined, off: number | null) =>
    local == null || off == null ? null : local + off;
  const bleServer = onServer(input.bleRxMs, phoneOff);
  const callServer = onServer(input.callStartMs, phoneOff);
  const arrivalServer = onServer(input.arrivalMs, tvOff);
  const frameServer = onServer(input.firstFrameMs, tvOff);
  const phoneErr = finite(input.phoneOffErrMs);
  const tvErr = finite(input.tvOffErrMs);
  return {
    bleToEnqueueMs: diff(input.enqueuedMs, input.bleRxMs),
    queueWaitMs: diff(input.callStartMs, input.enqueuedMs),
    callRttMs: diff(input.callEndMs, input.callStartMs),
    phoneToServerMs: diff(input.serverMs, callServer),
    serverCommitMs: diff(input.commitMs, input.serverMs),
    commitToTvMs: diff(arrivalServer, input.commitMs),
    tvToFrameMs: diff(input.firstFrameMs, input.arrivalMs),
    bleToTvRawMs: diff(input.arrivalMs, input.bleRxMs),
    bleToTvMs: diff(arrivalServer, bleServer),
    totalRawMs: diff(input.firstFrameMs, input.bleRxMs),
    totalMs: diff(frameServer, bleServer),
    skewErrMs: phoneErr == null && tvErr == null ? null : Math.round((phoneErr ?? 0) + (tvErr ?? 0)),
  };
}

export function tvLatencyStagesForRecord(record: TvReceiverLatencyRecord, callEndMs?: number | null): TvLatencyStages {
  return tvLatencyStages({
    bleRxMs: record.stamp?.bleRxMs ?? null,
    enqueuedMs: record.stamp?.enqueuedMs ?? null,
    callStartMs: record.stamp?.callStartMs ?? null,
    callEndMs: callEndMs ?? null,
    phoneOffMs: record.stamp?.offMs ?? null,
    phoneOffErrMs: record.stamp?.offErrMs ?? null,
    serverMs: record.serverMs,
    commitMs: record.commitMs,
    arrivalMs: record.arrivalMs,
    firstFrameMs: record.firstFrameMs,
    tvOffMs: record.offMs,
    tvOffErrMs: record.offErrMs,
  });
}

// ---------------------------------------------------------------------------
// The TV's recorder

/** The parts of a session document the recorder reads. */
export interface TvLatencyDoc {
  event?: unknown;
  sequence?: unknown;
  updatedAtMs?: unknown;
  /** A Firestore Timestamp (the serverTimestamp of the write). */
  updatedAt?: unknown;
}

function timestampMs(value: unknown): number | null {
  if (!value || typeof value !== 'object') return finite(value);
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis === 'function') return finite((toMillis as () => number).call(value));
  return null;
}

export interface TvLatencyRecorder {
  /** Every session snapshot, before anything else touches it. */
  noteSessionDoc: (doc: TvLatencyDoc, meta?: { fromCache?: boolean }) => void;
  /** The scene's first frame of a replay (`p:<event id>` on the TV). */
  noteReplayFirstFrame: (replayKey: string) => void;
  /** A heartbeat round trip, for the TV's own clock offset. */
  clock: ClockOffsetEstimator;
  records: () => readonly TvReceiverLatencyRecord[];
  /** Unreported records old enough to send (first frame drawn, or 8 s passed). */
  pendingReports: (nowMs?: number) => TvLatencyReport[];
  markReported: (ids: readonly string[]) => void;
  /** True once a putt arrived and the TV has no clock offset yet. */
  wantsEarlyHeartbeat: () => boolean;
  subscribe: (listener: () => void) => () => void;
  /** The receiver kind put on each record. */
  setReceiver: (rx: string) => void;
  reset: () => void;
}

export function createTvLatencyRecorder(options: { now?: () => number; capacity?: number } = {}): TvLatencyRecorder {
  const now = options.now ?? Date.now;
  const capacity = options.capacity ?? 24;
  const clock = createClockOffsetEstimator({ now });
  let list: TvReceiverLatencyRecord[] = [];
  let rx = 'web';
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((listener) => listener());

  return {
    clock,
    noteSessionDoc(doc, meta) {
      const event = doc.event as Record<string, unknown> | null | undefined;
      if (!event || typeof event !== 'object' || event.kind !== 'putt' || event.ghost === true) return;
      const id = typeof event.id === 'string' ? event.id : null;
      if (!id || list.some((record) => record.id === id)) return;
      const arrivalMs = now();
      const offset = clock.current(arrivalMs);
      list = [...list, {
        id,
        rx,
        stamp: readTvPuttLatencyStamp(event.lat),
        docSeq: finite(doc.sequence),
        serverMs: finite(doc.updatedAtMs),
        commitMs: timestampMs(doc.updatedAt),
        arrivalMs,
        firstFrameMs: null,
        fromCache: meta?.fromCache === true,
        offMs: offset?.offsetMs ?? null,
        offErrMs: offset?.errorMs ?? null,
        reported: false,
      }].slice(-capacity);
      emit();
    },
    noteReplayFirstFrame(replayKey) {
      if (!replayKey.startsWith('p:')) return;
      const id = replayKey.slice(2);
      const index = list.findIndex((record) => record.id === id);
      if (index < 0 || list[index].firstFrameMs != null) return;
      const next = [...list];
      next[index] = { ...next[index], firstFrameMs: now() };
      list = next;
      emit();
    },
    records: () => list,
    pendingReports(nowMs = now()) {
      return list
        .filter((record) => !record.reported && (record.firstFrameMs != null || nowMs - record.arrivalMs >= 8_000))
        .slice(-20)
        .map((record) => tvLatencyReport(record));
    },
    markReported(ids) {
      if (!ids.length) return;
      const set = new Set(ids);
      list = list.map((record) => (set.has(record.id) ? { ...record, reported: true } : record));
    },
    wantsEarlyHeartbeat: () => list.length > 0 && clock.current() == null,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setReceiver(next) {
      rx = next;
    },
    reset() {
      list = [];
      emit();
    },
  };
}

export function tvLatencyReport(record: TvReceiverLatencyRecord): TvLatencyReport {
  return {
    id: record.id.slice(0, 80),
    rx: record.rx,
    ble: round(record.stamp?.bleRxMs ?? null),
    call: round(record.stamp?.callStartMs ?? null),
    poff: round(record.stamp?.offMs ?? null),
    poffErr: round(record.stamp?.offErrMs ?? null),
    seq: record.stamp?.seq ?? null,
    docSeq: record.docSeq,
    srv: record.serverMs,
    cmt: record.commitMs,
    arr: record.arrivalMs,
    ff: record.firstFrameMs,
    off: record.offMs,
    offErr: record.offErrMs,
    cache: record.fromCache,
  };
}

/** Read a report back (the phone reads them off the session document). */
export function readTvLatencyReport(value: unknown): TvLatencyReport | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const arr = finite(raw.arr);
  if (typeof raw.id !== 'string' || !raw.id || arr == null) return null;
  return {
    id: raw.id,
    rx: typeof raw.rx === 'string' ? raw.rx : '?',
    ble: finite(raw.ble),
    call: finite(raw.call),
    poff: finite(raw.poff),
    poffErr: finite(raw.poffErr),
    seq: finite(raw.seq),
    docSeq: finite(raw.docSeq),
    srv: finite(raw.srv),
    cmt: finite(raw.cmt),
    arr,
    ff: finite(raw.ff),
    off: finite(raw.off),
    offErr: finite(raw.offErr),
    cache: raw.cache === true,
  };
}

function ms(value: number | null): string {
  return value == null ? '?' : String(value);
}

/** The diagnostics overlay's lines for the last few putts, newest first. */
export function tvLatencyLines(records: readonly TvReceiverLatencyRecord[], count = 4): string[] {
  const recent = records.slice(-count).reverse();
  if (!recent.length) return ['latency: no putt yet'];
  const lines = ['latency ms: ble>call · call>srv · srv>commit · commit>tv · tv>frame = total (raw)'];
  for (const record of recent) {
    const s = tvLatencyStagesForRecord(record);
    const bleToCall = diff(record.stamp?.callStartMs ?? null, record.stamp?.bleRxMs ?? null);
    const coalesced = record.stamp?.seq != null && record.docSeq != null && record.stamp.seq !== record.docSeq;
    const flags = [
      record.stamp ? '' : 'no phone stamp',
      record.stamp?.bleRxApprox ? 'ble approx' : '',
      coalesced ? 'coalesced' : '',
      record.fromCache ? 'cache' : '',
      s.skewErrMs != null ? `±${s.skewErrMs}` : 'skew unknown',
    ].filter(Boolean).join(' ');
    lines.push(
      `${record.id.slice(-6)} ${ms(bleToCall)} · ${ms(s.phoneToServerMs)} · ${ms(s.serverCommitMs)} · ${ms(s.commitToTvMs)} · ${ms(s.tvToFrameMs)} = ${ms(s.totalMs)} (${ms(s.totalRawMs)})${flags ? ` ${flags}` : ''}`,
    );
  }
  return lines;
}

/** The TV's one recorder. The scene and the session client feed it; the stage overlay reads it. */
export const tvLatencyRecorder = createTvLatencyRecorder();

/** Called by SceneContent on a replay's first frame. A no-op on the phone (no records). */
export function noteTvReplayFirstFrame(replayKey: string): void {
  tvLatencyRecorder.noteReplayFirstFrame(replayKey);
}
